import * as React from 'react';

import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';
import { AsyncBoundary } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $outboundTicketTab } from '../../../_state/scaleTicketPro.state';

import { OutboundTicketTabCustomerInfo } from './OutboundTicketTabCustomerInfo';
import { OutboundTicketTabDetails } from './OutboundTicketTabDetails';
import { OutboundTicketTabDocuments } from './OutboundTicketTabDocuments';
import { OutboundTicketTabMaterials } from './OutboundTicketTabMaterials';
import { OutboundTicketTabNotes } from './OutboundTicketTabNotes';
import { OutboundTicketTabPhotos } from './OutboundTicketTabPhotos';

import { SectionErrorBoundary } from '~/app/components/error/SectionErrorBoundary';
import { LoadReconciliationForm } from '~/modules/trade/_components/loads/LoadReconciliationForm';
import { LoadRecord } from '~/modules/trade/types/load';
import { ScaleTicketTab } from '~/modules/trade/types/scaleTickets';

type OutboundTicketTabsProps = {
  ticket: LoadRecord;
  header?: React.ReactNode;
  isLoading?: boolean;
};

type TabContent = Partial<Record<ScaleTicketTab, React.ReactNode>>;

const RECONCILIATION_ELIGIBLE_STATUSES: LoadStatus[] = [
  enumLoadStatus.Shipped,
  enumLoadStatus.Received,
  enumLoadStatus.Reconciled,
];

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET TABS ⚛️ ⚛️ ⚛️
//
export const OutboundTicketTabs = ({
  ticket,
  header,
  isLoading,
}: OutboundTicketTabsProps) => {
  // Atoms
  const tab = useAtomValue($outboundTicketTab);

  const status = ticket?.status as LoadStatus | undefined;
  const activeTab = (tab || 'materials') as ScaleTicketTab;
  const showReconciliation =
    !!status && RECONCILIATION_ELIGIBLE_STATUSES.includes(status);
  const isEditable = status !== enumLoadStatus.Voided;

  // Memoized values
  const tabContent = React.useMemo(
    (): TabContent => ({
      materials: <OutboundTicketTabMaterials load={ticket} />,
      notes: <OutboundTicketTabNotes />,
      photos: <OutboundTicketTabPhotos ticket={ticket} />,
      customerInfo: (
        <OutboundTicketTabCustomerInfo ticket={ticket} disabled={!isEditable} />
      ),
      details: (
        <OutboundTicketTabDetails ticket={ticket} disabled={!isEditable} />
      ),
      documents: <OutboundTicketTabDocuments ticket={ticket} />,
      ...(showReconciliation && {
        reconciliation: (
          <LoadReconciliationForm
            load={ticket}
            isDisabled={status === enumLoadStatus.Reconciled}
          />
        ),
      }),
      history: (
        <div className="p-4 text-foreground/50">History coming soon</div>
      ),
    }),
    [ticket, showReconciliation, status, isEditable],
  );

  return (
    <AsyncBoundary
      error={null}
      loading={isLoading}
      className="@OutboundTicketTabs"
    >
      {header}
      <SectionErrorBoundary key={activeTab}>
        <div className="min-h-0 flex-1">{tabContent[activeTab]}</div>
      </SectionErrorBoundary>
    </AsyncBoundary>
  );
};
